"use client";
import React, { useState, useEffect } from "react";
import { FiUser, FiCamera } from "react-icons/fi";
import { useProfile } from "../../context/ProfileContext";
import { UPLOAD_URL } from "../../config/apiConfig";
 
export default function ProfileForm() {
  const { profile, updateProfile, loading, setProfile } = useProfile();
 
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [profileImage, setProfileImage] = React.useState("");
  const [imageFile, setImageFile] = React.useState(null);
  const [isUploading, setIsUploading] = React.useState(false);
 
  const [email, setEmail] = React.useState("");

  const [mobile, setMobile] = React.useState("");

 
  const [status, setStatus] = React.useState(null);
  const [nameError, setNameError] = React.useState("");
  const [mobileError, setMobileError] = React.useState("");
  const fileInputRef = React.useRef(null);
 
  // Helper function to validate alphabetic input (allows letters, spaces, hyphens, and apostrophes)
  const isValidNameCharacter = (char) => {
    return /^[a-zA-Z\s\-']+$/.test(char);
  };
 

  // Helper function to filter and validate name input
  const validateNameInput = (value) => {
    // Remove any non-alphabetic characters except spaces, hyphens, and apostrophes
    const filteredValue = value.replace(/[^a-zA-Z\s\-']/g, '');
 
    // Trim excessive spaces and ensure no double spaces
    const cleanedValue = filteredValue.replace(/\s+/g, ' ').trim();
 
    return cleanedValue;
  };
 
  // Helper function to validate mobile number input (only digits, max 10 digits)
  const validateMobileInput = (value) => {
    // Remove any non-numeric characters
    const numericValue = value.replace(/[^0-9]/g, '');
 
    // Limit to 10 digits
    return numericValue.slice(0, 10);
  };
 
  // Enhanced onChange handler for first name
  const handleFirstNameChange = (e) => {
    const inputValue = e.target.value;
    const validatedValue = validateNameInput(inputValue);
 
    // Show error if invalid characters were attempted
    if (inputValue !== validatedValue && inputValue.length > 0) {
      setNameError("Only letters, spaces, hyphens, and apostrophes are allowed in names");
      setTimeout(() => setNameError(""), 3000); // Clear error after 3 seconds
    } else {
      setNameError("");
    }
 
    setFirstName(validatedValue);
  };
 
  // Enhanced onChange handler for last name
  const handleLastNameChange = (e) => {
    const inputValue = e.target.value;
    const validatedValue = validateNameInput(inputValue);
 
    // Show error if invalid characters were attempted
    if (inputValue !== validatedValue && inputValue.length > 0) {
      setNameError("Only letters, spaces, hyphens, and apostrophes are allowed in names");
      setTimeout(() => setNameError(""), 3000); // Clear error after 3 seconds
    } else {
      setNameError("");
    }
 
    setLastName(validatedValue);
  };
 
  // Enhanced onChange handler for mobile number
  const handleMobileChange = (e) => {
    const inputValue = e.target.value;
    const validatedValue = validateMobileInput(inputValue);
 
    // Show error if mobile number is not exactly 10 digits
    if (validatedValue.length > 0 && validatedValue.length !== 10) {
      setMobileError("Mobile number must be exactly 10 digits");
    } else {
      setMobileError("");
    }
 
    setMobile(validatedValue);
  };
 
  // Populate profile data
  React.useEffect(() => {
    if (profile) {
      setFirstName(profile.first_name || "");
      setLastName(profile.last_name || "");
      if (profile.image) setProfileImage(`${UPLOAD_URL}/users/${profile.image}`);
      if (profile.email) setEmail(profile.email);
      if (profile.mobile) {
       
          setMobile(profile.mobile);
        
      }
    }
  }, [profile]);
 
  // Submit profile update
  const handleSubmit = async (e) => {
    e.preventDefault();
    setStatus(null);
 
    // Validation
    if (!firstName.trim()) {
      setStatus("❌ First name is required");
      return;
    }
    if (!lastName.trim()) {
      setStatus("❌ Last name is required");
      return;
    }
    if (!/^[a-zA-Z\s\-']+$/.test(firstName.trim())) {
      setStatus("❌ First name can only contain letters, spaces, hyphens, and apostrophes");
      return;
    }
    if (!/^[a-zA-Z\s\-']+$/.test(lastName.trim())) {
      setStatus("❌ Last name can only contain letters, spaces, hyphens, and apostrophes");
      return;
    }
    if (!email.trim()) {
      setStatus("❌ Email is required");
      return;
    }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setStatus("❌ Please enter a valid email address");
      return;
    }
    if (!mobile.trim()) {
      setStatus("❌ Mobile number is required");
      return;
    }
    if (mobile.length !== 10) {
      setStatus("❌ Mobile number must be exactly 10 digits");
      return;
    }
 
    // Clear any existing errors
    setMobileError("");
    setNameError("");
 
    try {
      const formData = new FormData();
      formData.append("first_name", firstName.trim());
      formData.append("last_name", lastName.trim());
      formData.append("email", email);
      formData.append("mobile", mobile);

      if (imageFile) formData.append("image", imageFile);
 
      const result = await updateProfile(formData);
 
      if (result.success) {
        const updated = result.data;
        setStatus("✅ Profile updated successfully!");
        setImageFile(null);
        if (updated.mobile) {
         
            setMobile(updated.mobile);
          
        }
        if (updated.image) {
          setProfileImage(`${UPLOAD_URL}/users/${updated.image}`);
        }
 
        setProfile(updated); // ✅ Updates global context
      } else {
        setStatus(`❌ Failed: ${result.message}`);
      }
    } catch (err) {
      setStatus(`❌ Error: ${err.message}`);
    }
  };
 
  // Profile image upload
  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      setImageFile(file);
      setIsUploading(true);
 
      const reader = new FileReader();
      reader.onload = (e) => {
        setProfileImage(e.target.result);
        setIsUploading(false);
      };
      reader.readAsDataURL(file);
    }
  };
 
  const handleImageClick = () => fileInputRef.current?.click();
 
  // Send OTP with validation
  // const handleSave = async (type) => {
  //   if (type === "email") {
  //     if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
  //       alert("Please enter a valid email address");
  //       return;
  //     }
  //     const res = await sendOtp({ email }, "email");
  //     if (res.status === 200) {
  //       setModalType("email");
  //       setShowModal(true);
  //     } else {
  //       alert(res.message || "Failed to send OTP");
  //     }
  //   } else if (type === "phone") {
  //     if (mobile.length !== 10) {
  //       alert("Mobile number must be exactly 10 digits");
  //       return;
  //     }
  //     const fullMobile = `${countryCode} ${mobile}`;
  //     const res = await sendOtp({ mobile: fullMobile }, "mobile");
  //     if (res.status === 200) {
  //       setModalType("phone");
  //       setShowModal(true);
  //     } else {
  //       alert(res.message || "Failed to send OTP");
  //     }
  //   }
  // };
 
  // const handleConfirmCode = async () => {
  //   const otpCode = otp.join("");
  //   if (otpCode.length !== 6) return alert("Enter all 6 digits");
 
  //   let res;
  //   if (modalType === "email") {
  //     res = await verifyEmailOtp({ email, otp: otpCode });
  //   } else if (modalType === "phone") {
  //     res = await verifyMobileOtp({ mobile: `${countryCode} ${mobile}`, otp: otpCode });
  //   }
 
  //   if (res?.status === 200) {
  //     setIsVerified(true);
  //     setProfile((prev) => ({
  //       ...prev,
  //       ...(modalType === "email" ? { email } : { mobile: `${countryCode} ${mobile}` }),
  //     }));
 
  //     setTimeout(() => {
  //       setShowModal(false);
  //       setIsVerified(false);
  //       setOtp(Array(6).fill(""));
  //     }, 2000);
  //     window.location.reload(); // Refresh the page to update verification status
  //   } else {
  //     alert(res?.message || "Invalid OTP");
  //   }
  // };
 
  // const handleOtpChange = (val, idx) => {
  //   if (/^[0-9]?$/.test(val)) {
  //     const newOtp = [...otp];
  //     newOtp[idx] = val;
  //     setOtp(newOtp);
  //   }
  // };
 
  if (loading) return <div className="text-center p-10 text-gray-500">Loading profile...</div>;
 
  return (
    <main className="ml-5 w-[73%] max-md:ml-0 max-md:w-full">
      <div className="w-full max-md:mt-8 max-md:max-w-full">
        <header className="flex gap-2.5 items-center py-1.5 text-2xl font-bold text-center border-b border-black border-opacity-10 min-h-12 text-sky-950">
          <h2 className="self-stretch my-auto text-sky-950">Edit Profile</h2>
        </header>
 
        <section className="flex flex-col justify-center items-center px-4 sm:px-8 md:px-12 lg:px-20 py-6 sm:py-8 md:py-11 mt-7 text-base text-black bg-white rounded-2xl shadow-2xl max-md:px-5 max-md:max-w-full">
          <form onSubmit={handleSubmit} className="flex flex-col w-full max-w-md sm:max-w-lg md:max-w-xl lg:max-w-[335px]">
            {/* Profile Image */}
            <div className="flex flex-col items-center mb-6 sm:mb-8">
              <div className="relative group cursor-pointer" onClick={handleImageClick}>
                {profileImage ? (
                  <img
                    src={profileImage}
                    alt="Profile"
                    className="object-contain self-center max-w-full aspect-square w-20 sm:w-24 md:w-28 lg:w-[100px] rounded-full transition-opacity group-hover:opacity-90"
                  />
                ) : (
                  <div className="flex items-center justify-center w-20 sm:w-24 md:w-28 lg:w-[100px] h-20 sm:h-24 md:h-28 lg:h-[100px] bg-gray-200 rounded-full transition-opacity group-hover:opacity-90">
                    <FiUser className="text-gray-500 text-2xl sm:text-3xl md:text-4xl lg:text-5xl" />
                  </div>
                )}
                {isUploading && (
                  <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-full">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                  </div>
                )}
                <div className="absolute bottom-0 right-0 bg-[#002347] rounded-full p-1.5 sm:p-2 shadow-lg border-2 border-gray-400 hover:bg-gray-50 transition-colors">
                  <FiCamera className="text-white text-sm sm:text-base md:text-lg" />
                </div>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                className="hidden"
                aria-label="Upload profile image"
              />
            </div>
 
            {/* Name Fields */}
            <div className="flex flex-col gap-4 sm:gap-5 mb-5 sm:mb-6">
              <div className="flex flex-col">
                <input
                  type="text"
                  value={firstName}
                  onChange={handleFirstNameChange}
                  placeholder="First Name"
                  className="border-b border-black border-opacity-20 focus:border-b-red-500 py-2 px-1 text-sm sm:text-base w-full focus:outline-none"
                  required
                  aria-label="First Name"
                  aria-describedby={nameError ? "name-error" : undefined}
                />
              </div>
              <div className="flex flex-col">
                <input
                  type="text"
                  value={lastName}
                  onChange={handleLastNameChange}
                  placeholder="Last Name"
                  className="border-b border-black border-opacity-20 focus:border-b-red-500 py-2 px-1 text-sm sm:text-base w-full focus:outline-none"
                  required
                  aria-label="Last Name"
                  aria-describedby={nameError ? "name-error" : undefined}
                />
              </div>
              {nameError && (
                <div
                  id="name-error"
                  className="text-red-500 text-xs sm:text-sm mt-1"
                  role="alert"
                  aria-live="polite"
                >
                  {nameError}
                </div>
              )}
            </div>
 
            {/* Email & Mobile */}
            <div className="flex flex-col gap-4 sm:gap-5 mb-5 sm:mb-6">
              {/* Email Section */}
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="Email"
                    className="flex-1 min-w-0 border-b border-black border-opacity-20 focus:border-b-red-500 py-2 px-1 text-xs sm:text-sm md:text-base focus:outline-none"
                    required
                    aria-label="Email address"
                  />
                  {/* {profile?.email_verified === "yes" ? (
                    <button type="button" className="text-green-500 font-medium px-2 sm:px-3 py-2 text-xs sm:text-sm md:text-base whitespace-nowrap flex-shrink-0" disabled>
                      Verified
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="text-red-500 hover:text-red-600 transition-colors px-2 sm:px-3 py-2 rounded text-xs sm:text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-red-500 whitespace-nowrap flex-shrink-0 font-medium"
                      onClick={() => handleSave("email")}
                      aria-label="Verify email address"
                    >
                      Verify
                    </button>
                  )} */}
                </div>
              </div>
 
              {/* Country Code */}
              {/* <select
  value={countryCode}
  onChange={(e) => setCountryCode(e.target.value)}
  className="border-b border-black border-opacity-20 focus:border-b-red-500 py-2 px-1 text-xs sm:text-sm md:text-base w-full focus:outline-none"
  aria-label="Country code"
>
  <option value="">Select country code</option>
  <option value="+1">+1 (US/Canada)</option>
  <option value="+91">+91 (India)</option>
  <option value="+44">+44 (UK)</option>
  <option value="+61">+61 (Australia)</option>
  <option value="+81">+81 (Japan)</option>
</select> */}
 
 
              {/* Mobile Section */}
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <input
                    type="tel"
                    value={mobile}
                    onChange={handleMobileChange}
                    placeholder="Mobile Number (10 digits)"
                    className={`flex-1 min-w-0 border-b border-opacity-20 py-2 px-1 text-xs sm:text-sm md:text-base focus:outline-none ${
                      mobileError ? 'border-red-500' : 'border-black focus:border-b-red-500'
                    }`}
                    required
                    maxLength="10"
                    aria-label="Mobile number (exactly 10 digits required)"
                    aria-describedby={mobileError ? "mobile-error" : undefined}
                  />
                  {/* {profile?.email_verified === "yes" ? (
                    <button type="button" className="text-green-500 font-medium px-2 sm:px-3 py-2 text-xs sm:text-sm md:text-base whitespace-nowrap flex-shrink-0" disabled>
                      Verified
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="text-red-500 hover:text-red-600 transition-colors px-2 sm:px-3 py-2 rounded text-xs sm:text-sm md:text-base focus:outline-none focus:ring-2 focus:ring-red-500 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap flex-shrink-0 font-medium"
                      onClick={() => handleSave("phone")}
                      disabled={mobile.length !== 10}
                      aria-label="Verify mobile number"
                    >
                      Verify
                    </button>
                  )} */}
                </div>
                {mobileError && (
                  <div
                    id="mobile-error"
                    className="text-red-500 text-xs sm:text-sm"
                    role="alert"
                    aria-live="polite"
                  >
                    {mobileError}
                  </div>
                )}
              </div>
            </div>
 
            {/* Submit */}
            <button
              type="submit"
              className="bg-green-500 text-white py-2.5 sm:py-3 rounded-lg hover:bg-green-600 transition-colors focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base font-medium"
              disabled={isUploading}
              aria-label="Save and update profile"
            >
              {isUploading ? "Uploading..." : "Save & Update"}
            </button>
 
            {status && (
              <div
                className="mt-4 text-center text-xs sm:text-sm font-medium text-gray-700 px-2"
                role="status"
                aria-live="polite"
              >
                {status}
              </div>
            )}
          </form>
        </section>
      </div>
 
      {/* OTP Verification Modal */}
      {/* {showModal && (
        <div
          className="fixed inset-0 bg-black/50 backdrop-blur-lg flex items-center justify-center z-50 p-4"
          role="dialog"
          aria-modal="true"
          aria-labelledby="otp-modal-title"
        >
          <div className="bg-white w-full max-w-sm sm:max-w-md md:max-w-lg lg:max-w-xl xl:max-w-[500px] rounded-2xl shadow-lg p-4 sm:p-6 relative mx-4">
            {isVerified ? (
              <div className="flex flex-col items-center justify-center space-y-4 py-6">
                <h2
                  id="otp-modal-title"
                  className="text-base sm:text-lg font-semibold text-gray-800 text-center"
                >
                  {modalType === "email" ? "Email Verified!" : "Mobile Verified!"}
                </h2>
                <p className="text-xs sm:text-sm text-gray-500 text-center">Closing in 2 seconds...</p>
              </div>
            ) : (
              <>
                <h2
                  id="otp-modal-title"
                  className="text-center text-base sm:text-lg font-semibold text-gray-800 mb-2"
                >
                  Enter Verification Code
                </h2>
                <p className="text-center text-xs sm:text-sm text-gray-600 mb-4 px-2">
                  We've sent a 6-digit code to your {modalType === "email" ? "email" : "mobile number"}
                </p>
                <div className="flex justify-center space-x-2 sm:space-x-3 mb-6 px-2">
                  {otp.map((digit, i) => (
                    <input
                      key={i}
                      type="text"
                      maxLength="1"
                      value={digit}
                      onChange={(e) => handleOtpChange(e.target.value, i)}
                      className="w-8 h-10 sm:w-10 sm:h-12 text-center border-b-2 border-gray-300 focus:border-green-500 text-base sm:text-lg focus:outline-none"
                      aria-label={`Digit ${i + 1} of verification code`}
                      pattern="[0-9]"
                      title="Enter a single digit"
                    />
                  ))}
                </div>
                <button
                  onClick={handleConfirmCode}
                  className="bg-green-500 hover:bg-green-600 text-white w-full py-2.5 sm:py-3 rounded-lg mb-3 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 text-sm sm:text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={otp.join("").length !== 6}
                  aria-label="Confirm verification code"
                >
                  Confirm
                </button>
                <button
                  onClick={() => {
                    setShowModal(false);
                    setOtp(Array(6).fill(""));
                  }}
                  className="text-gray-500 hover:text-gray-700 w-full py-2 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 text-sm sm:text-base font-medium"
                  aria-label="Cancel verification"
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      )} */}
    </main>
  );
}